<template>
	<section
		class="scene-tv"
		v-bind:class="{
			'state-player-ready': playerReady,
		}"
	>
		<section class="static">
			<ChalkyBrandingBadge size="md" />
		</section>

		<ChalkyTvVideoTheater
			ref="viewVideoTheater"
			v-bind:channel="activeChannel"
			v-bind:key="$route.params.id"
			v-bind:scheduleCollection="scheduleCollection"
			v-bind:scheduleModel="liveScheduleModel"
			v-on:player:ended="Handle_OnPlayerEnded"
			v-on:player:error="Handle_OnPlayerError"
			v-on:schedule:set="Handle_OnScheduleSet"
			v-on:video:playing="Handle_OnVideoPlaying"
		/>

		<section class="ui-wrapper">
			<ChalkyTvAttributionBanner
				v-bind:key="scheduleModel.id"
				v-bind:scheduleModel="scheduleModel"
				v-if="hasChannel"
			/>

			<ChalkyTvAttributionBannerMissing
				v-bind:key="scheduleModel.id"
				v-bind:scheduleModel="scheduleModel"
				v-else
			/>
		</section>

		<ChalkyTvSchedule
			direction="vertical"
			timeFormat="12h-short"
			v-bind:activeChannel="activeChannel"
			v-bind:activeScheduleModel="liveScheduleModel"
			v-bind:key="liveScheduleModel.uniqueKey"
			v-on:select:channel="Handle_OnSelectChannel"
			v-on:select:schedule="Handle_OnSelectSchedule"
		/>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneTV extends SceneBase {
		/**
		 * @var any
		 */
		@Ref('viewVideoTheater')
		private viewVideoTheater!: any;

		/**
		 * @return ChalkySticks.Enum.GameType
		 */
		protected get defaultChannel(): ChalkySticks.Enum.GameType {
			return ChalkySticks.Enum.GameType.EightBall;
		}

		/**
		 * @return boolean
		 */
		protected get hasLiveStream(): boolean {
			return this.liveScheduleModel != null && !!this.liveScheduleModel.id;
		}

		/**
		 * @type ChalkySticks.Collection.Schedule
		 */
		@Prop()
		public scheduleCollection!: ChalkySticks.Collection.Schedule;

		/**
		 * @type ChalkySticks/Model/Schedule
		 */
		@Prop()
		public scheduleModel!: ChalkySticks.Model.Schedule;

		/**
		 * @var ChalkySticks.Enum.GameType | null
		 */
		protected activeChannel: ChalkySticks.Enum.GameType | null = ChalkySticks.Enum.GameType.EightBall;

		/**
		 * @type boolean
		 */
		protected hasChannel: boolean = false;

		/**
		 * @var ChalkySticks.TV.Collection.Live
		 */
		protected liveScheduleCollection: ChalkySticks.TV.Collection.Live = new ChalkySticks.TV.Collection.Live({
			baseUrl: ChalkySticks.Core.Constants.API_URL_V3,
		});

		/**
		 * @var ChalkySticks.Model.Schedule
		 */
		protected liveScheduleModel: ChalkySticks.Model.Schedule = ChalkySticks.Factory.Schedule.model();

		/**
		 * @type boolean
		 */
		protected playerReady: boolean = false;

		/**
		 * @return Promise<void>
		 */
		@mounted
		public async ensureDefaultRoute(): Promise<void> {
			if (!this.$route.params?.id) {
				this.gotoChannel(this.defaultChannel);
			}
		}

		/**
		 * @return Promise<ChalkySticks.TV.Collection.Live>
		 */
		@mounted
		public async fetchLiveCollection(): Promise<ChalkySticks.TV.Collection.Live> {
			if (this.liveScheduleCollection.length > 0) {
				return this.liveScheduleCollection;
			}

			// Load live schedule collection
			await this.liveScheduleCollection.fetch();

			return this.liveScheduleCollection;
		}

		/**
		 * Change the active channel and update the route. The route watcher
		 * will handle the actual state changes.
		 *
		 * @param ChalkySticks.Model.Schedule scheduleModel
		 * @param ChalkySticks.Enum.GameType channel
		 * @return Promise<void>
		 */
		protected async changeChannel(
			scheduleModel: ChalkySticks.Model.Schedule | null,
			channel: ChalkySticks.Enum.GameType,
		): Promise<void> {
			// Check if we're on this page already
			if (this.activeChannel === channel && !!channel) {
				return;
			}

			// Fade out
			this.playerReady = false;

			// Wait
			await ChalkySticks.Utility.sleep(250);

			// Determine route ID: named channel or schedule model ID
			const id = channel || scheduleModel?.id || this.defaultChannel;

			// Track
			ChalkySticks.Utility.Analytics.track('tv:channel.change', {
				channel: id,
			});

			// Push to route - the watcher handles the rest
			this.gotoChannel(id);
		}

		/**
		 * @param string id
		 * @return void
		 */
		protected gotoChannel(id: string): void {
			this.$router
				.replace({
					name: 'PageTVSpecific',
					params: {
						id,
					},
				})
				.catch(() => {
					// Suppress NavigationDuplicated error
				});
		}

		/**
		 * Tell the video theater to set the active channel
		 *
		 * @return void
		 */
		protected setActiveChannel(): void {
			// Not ready yet
			if (!this.viewVideoTheater) {
				setTimeout(() => this.setActiveChannel(), 1000);
				return;
			}

			// Don't set by game
			if (this.activeChannel === null) {
				return;
			}

			this.viewVideoTheater.setByGame(this.activeChannel);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnPlayerEnded(): Promise<void> {
			if (this.hasLiveStream) {
				this.gotoChannel(this.defaultChannel);
			}
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnPlayerError(): Promise<void> {
			if (this.hasLiveStream) {
				this.gotoChannel(this.defaultChannel);
			}
		}

		/**
		 * @param string channel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectChannel(channel: string): Promise<void> {
			this.changeChannel(null, channel as ChalkySticks.Enum.GameType);
		}

		/**
		 * @param ChalkySticks.Model.Schedule scheduleModel
		 * @param ChalkySticks.Enum.GameType channel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectSchedule(
			scheduleModel: ChalkySticks.Model.Schedule,
			channel: ChalkySticks.Enum.GameType,
		): Promise<void> {
			this.changeChannel(scheduleModel, channel);

			// If we're horizontal with a long tv guide, go to top
			ChalkySticks.Core.Event.Bus.dispatch('scrollToTop', { smooth: true });
		}

		/**
		 * @param ChalkySticks.Model.Schedule scheduleModel
		 * @return Promise<void>
		 */
		protected async Handle_OnScheduleSet(scheduleModel: ChalkySticks.Model.Schedule): Promise<void> {
			// Reset channel id
			this.scheduleModel.channel.id = '0';

			this.scheduleModel.set({
				...scheduleModel.attributes,
			});

			this.hasChannel = !!scheduleModel.channel.id;

			Vue.set(this.scheduleModel, 'id', scheduleModel.id);
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoPlaying(e: Event): Promise<void> {
			this.playerReady = true;
		}

		// endregion: Event Handlers

		// region: Watchers
		// ---------------------------------------------------------------------------

		/**
		 * Handle route changes to update the active channel.
		 *
		 * @param Route to
		 * @param Route from
		 * @return Promise<void>
		 */
		@Watch('$route', { immediate: true })
		protected async Handle_OnChangeRoute(to: any, from: any): Promise<void> {
			const id = to.params?.id;

			// Check if the ID is a valid GameType (named channel)
			const gameTypes = Object.values(ChalkySticks.Enum.GameType);
			const isNamedChannel = gameTypes.includes(id);
			const channel = id;

			// Set channel
			this.activeChannel = channel || this.defaultChannel;

			// Named channel (e.g., 8-ball, 9-ball)
			if (isNamedChannel) {
				this.setActiveChannel();
				this.liveScheduleModel = ChalkySticks.Factory.Schedule.model();
			} else if (id) {
				const scheduleCollection = await this.fetchLiveCollection();
				const scheduleModel = scheduleCollection.findWhere({ id });

				if (scheduleModel) {
					Object.freeze(scheduleModel);
					this.liveScheduleModel = scheduleModel;
				}
			}
		}

		// endregion: Watchers
	}
</script>

<style lang="scss">
	.scene-tv {
		.static {
			align-items: center;
			background: url('@/Asset/image/graphic/static.gif') no-repeat center center;
			display: flex;
			justify-content: center;
			mix-blend-mode: overlay;
			opacity: 0.5;
			z-index: 100;
		}

		.chalky.tv-videotheater {
			opacity: 1;
			position: relative;
			transition: opacity 0.25s var(--ease-in-out-quad);
			z-index: 5;
		}

		.chalky.tv-attributionbanner,
		.chalky.tv-attributionbannermissing {
			background-color: var(--chalky-blue);
			padding: 0 1rem;
			position: relative;
			transition: opacity 0.75s var(--ease-in-out-quad);
			z-index: 5;
		}

		.chalky.tv-schedule {
			--chalky-tv-schedule-header-size: 60px;
			position: relative;
			z-index: 5;

			// mk: temporary fix
			&.direction-vertical .now {
				width: 200%;
			}

			picture {
				&:before {
					transform: translate(80%, -33%);
				}

				img {
					height: 24px;
					width: auto;
				}
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.scene-tv:not(.state-player-ready) {
		.chalky.tv-videotheater {
			opacity: 0;
		}
	}

	.scene-tv.state-player-ready {
		.static {
			z-index: 1;
		}
	}

	// Media Queries
	// ---------------------------------------------------------------------------

	@media (orientation: portrait) {
		.scene-tv {
			display: grid;
			grid-template-areas:
				'videotheater'
				'attribution'
				'schedule';
			grid-template-columns: 1fr;
			grid-template-rows: 350px 50px 1fr;
			height: calc(100svh - var(--global-tab-height));

			.static {
				height: 350px;
				position: absolute;
				top: 0;
			}

			.static,
			.chalky.tv-videotheater {
				width: 100%;
			}

			.chalky.tv-videotheater {
				background-color: #010102;
				grid-area: videotheater;
			}

			.chalky.tv-attributionbanner {
				grid-area: attribution;
			}

			.chalky.tv-schedule {
				bottom: 0;
				grid-area: schedule;
				overflow: auto;
			}

			.tv-scheduleitem {
				.title {
					font-size: 1rem;
				}

				.subtitle {
					font-size: 0.75rem;
					line-height: 1.25;
				}
			}
		}
	}

	@media (orientation: landscape) {
		.scene-tv {
			right: 0 !important;

			.chalky.tv-videotheater {
				aspect-ratio: initial;
				height: 100dvh;
			}

			.static,
			#GlobalHeader,
			#GlobalNavigationTabs {
				display: none;
			}
		}
	}
</style>
